/**
 * Blog Listing Grid — client-side pagination + category filtering.
 *
 * Why client-side?
 * All articles are rendered into the DOM at once (paginate by 100 in Liquid).
 * This lets category-filter tabs show their hand-picked articles regardless of
 * their position in the blog — the old server-side paginate(by N) approach only
 * included the first N articles in the DOM, so any category article sitting
 * beyond position N was invisible to the filter.
 *
 * Both the "All" tab and category tabs respect the postsPerPage setting and
 * show numbered JS pagination when there are more results than one page.
 */
class BlogListingGrid extends HTMLElement {
  connectedCallback() {
    this.gridEl = this.querySelector('[data-blog-grid]');
    if (!this.gridEl) return;

    this.headingEl = this.querySelector('.blog-listing-grid__heading');
    this.defaultHeading =
      (this.headingEl && this.headingEl.getAttribute('data-default-heading')) ||
      this.getAttribute('data-default-heading') ||
      null;

    this.postsPerPage = parseInt(this.getAttribute('data-posts-per-page'), 10) || 6;
    this.currentPage = 1;
    this.currentFilter = 'all';
    this.currentArticleIds = [];

    this._onFilterChange = this._onFilterChange.bind(this);
    document.addEventListener('blog:filter:change', this._onFilterChange);

    this._onPaginationClick = this._onPaginationClick.bind(this);
    this.addEventListener('click', this._onPaginationClick);

    // Apply initial "All" view on load
    this._applyView();
  }

  disconnectedCallback() {
    document.removeEventListener('blog:filter:change', this._onFilterChange);
    this.removeEventListener('click', this._onPaginationClick);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  _getAllCards() {
    return Array.from(this.gridEl.querySelectorAll('.blog-card'));
  }

  /** Returns the subset of cards that match the current filter. */
  _getMatchingCards(tag, articleIds) {
    const cards = this._getAllCards();

    if (tag === 'all') return cards;

    if (articleIds.length > 0) {
      return cards.filter((c) => articleIds.includes(c.getAttribute('data-article-id') || ''));
    }

    // tag-string fallback (legacy — not used by the current category-tab setup)
    return cards.filter((c) => {
      const tagStr = ',' + (c.getAttribute('data-tags') || '') + ',';
      return tagStr.indexOf(',' + tag + ',') !== -1;
    });
  }

  // ─── View ─────────────────────────────────────────────────────────────────────

  _applyView() {
    const allCards = this._getAllCards();
    const matching = this._getMatchingCards(this.currentFilter, this.currentArticleIds);
    const matchingIds = matching.map((c) => c.getAttribute('data-article-id'));

    const start = (this.currentPage - 1) * this.postsPerPage;
    const end = start + this.postsPerPage;

    // Determine which matching cards belong to the current page
    const pageIds = new Set(matchingIds.slice(start, end));

    allCards.forEach((card) => {
      const id = card.getAttribute('data-article-id');
      if (pageIds.has(id)) {
        card.classList.remove('blog-card--hidden');
      } else {
        card.classList.add('blog-card--hidden');
      }
    });

    // Show pagination whenever there are more matching articles than one page
    this._renderPagination(matching.length);
  }

  // ─── JS Pagination ───────────────────────────────────────────────────────────

  _renderPagination(totalCards) {
    const paginationEl = this.querySelector('[data-js-pagination]');
    if (!paginationEl) return;

    const totalPages = Math.ceil(totalCards / this.postsPerPage);

    if (totalPages <= 1) {
      paginationEl.style.display = 'none';
      paginationEl.innerHTML = '';
      return;
    }

    paginationEl.style.display = '';
    paginationEl.innerHTML = this._buildPaginationHTML(totalPages);
  }

  _buildPaginationHTML(totalPages) {
    const cur = this.currentPage;
    let html = '';

    // ── Previous arrow ──
    if (cur > 1) {
      html += `<a href="#" class="blg-pagination__arrow" data-page="${cur - 1}" aria-label="Previous page">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg></a>`;
    } else {
      html += `<span class="blg-pagination__arrow blg-pagination__arrow--disabled" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg></span>`;
    }

    // ── Page numbers with ellipsis ──
    html += `<div class="blg-pagination__pages">`;
    let lastRendered = 0;

    for (let p = 1; p <= totalPages; p++) {
      const isVisible = p === 1 || p === totalPages || Math.abs(p - cur) <= 1;

      if (!isVisible) continue;

      if (lastRendered > 0 && p - lastRendered > 1) {
        html += `<span class="blg-pagination__ellipsis" aria-hidden="true">&hellip;</span>`;
      }

      if (p === cur) {
        html += `<span class="blg-pagination__page blg-pagination__page--active" aria-current="page">${p}</span>`;
      } else {
        html += `<a href="#" class="blg-pagination__page" data-page="${p}" aria-label="Page ${p}">${p}</a>`;
      }

      lastRendered = p;
    }

    html += `</div>`;

    // ── Next arrow ──
    if (cur < totalPages) {
      html += `<a href="#" class="blg-pagination__arrow" data-page="${cur + 1}" aria-label="Next page">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg></a>`;
    } else {
      html += `<span class="blg-pagination__arrow blg-pagination__arrow--disabled" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg></span>`;
    }

    return html;
  }

  _onPaginationClick(e) {
    const link = e.target.closest('[data-js-pagination] a[data-page]');
    if (!link) return;

    e.preventDefault();
    const page = parseInt(link.getAttribute('data-page'), 10);
    if (!page) return;

    this.currentPage = page;
    this.gridEl.classList.add('blog-listing-grid__articles--fading');

    setTimeout(() => {
      this._applyView();
      this.gridEl.classList.remove('blog-listing-grid__articles--fading');
    }, 300);
  }

  // ─── Category Filter ─────────────────────────────────────────────────────────

  _onFilterChange(e) {
    this.currentFilter = e.detail.tag;
    this.currentArticleIds = (e.detail.articles || '').split(',').filter(Boolean);
    this.currentPage = 1;

    this.gridEl.classList.add('blog-listing-grid__articles--fading');

    setTimeout(() => {
      this._applyView();

      if (this.headingEl) {
        if (this.currentFilter === 'all') {
          if (this.defaultHeading) this.headingEl.textContent = this.defaultHeading;
        } else {
          const activeTab = document.querySelector('.blog-filter-tab--active');
          if (activeTab) this.headingEl.textContent = activeTab.textContent.trim();
        }
      }

      this.gridEl.classList.remove('blog-listing-grid__articles--fading');
    }, 300);
  }
}

customElements.define('blog-listing-grid', BlogListingGrid);
